import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import moment, {Moment} from 'moment'
import {Calendar, CalendarViewChangeEvent} from 'primereact/calendar'
import {Nullable} from 'primereact/ts-helpers'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useParams} from 'react-router-dom'

import {Spinner} from '../../../App/Components'
import {DateTemplate} from '../../../App/Util/PrimeCalendarUtil/DateTemplate'
import {Box} from '../../../Common/Box'
import {Button} from '../../../Common/Button'
import {Flex} from '../../../Common/Flex'
import {useWindowDimensions} from '../../../Common/Hooks/useWindowDimensions'
import {Layout} from '../../../Common/Layout'
import {Spinner as SpinnerNew} from '../../../Common/Spinner'
import {TextArea} from '../../../Common/TextInput'
import {H1, H4} from '../../../Common/Typography'
import {PersonSelect} from '../../AbsenceNew/component/PersonSelect'
import {createAbsencePayload, IAbsencePayload,} from '../../AbsenceNew/util/createAbsencePayload'
import {useGetAbsenceCalendarDataQuery} from '../../AbsenceNew/util/useAbsenceQuery'
import {AbsenceTypeSelect} from '../../AbsenceTypeSelect/AbsenceTypeSelect'
import {useGeAbsenceTypeListQuery} from '../../AbsenceTypeSelect/useAbsenceTypeQuery'
import useAbsenceTypeService from '../../AbsenceTypeSelect/useAbsenceTypeService'
import {createGeneralAbsencePayload} from '../../GeneralAbsence/util/createGeneralAbsencePayload'
import {useGetGeneralAbsenceCalendarDataQuery} from '../../GeneralAbsence/util/useGeneralAbsenceQuery'

import {IAbsenceFormBasePayload, IGenerateAbsenceBasePayload,} from './IAbsenceFormBasePayload'

export interface IAbsenceFormBaseData {
  endsAt: Moment
  startsAt: Moment
  absenceTypeUuid: string
  reason: string
  personUuid?: string
}
interface IBaseFormProps {
  type: 'general-absence' | 'absence' | 'absence-request'
  data?: IAbsenceFormBaseData
  overviewPath: string
  pageTitle: string
  isSelectPerson: boolean
  requestStatus: 'pending' | 'error' | 'idle' | 'success'
}
// GENERAL ABSENCE interface
export interface IGeneralAbsenceProps extends IBaseFormProps {
  type: 'general-absence'
  newPayload: (payload: IAbsenceFormBasePayload) => void
}
// ABSENCE REQUEST interface
export interface IAbsenceRequestProps extends IBaseFormProps {
  type: 'absence-request'
  newPayload: (payload: IAbsenceFormBasePayload) => void
}
// ABSENCE interface
export interface IAbsenceProps extends IBaseFormProps {
  type: 'absence'
  newPayload: (payload: IAbsencePayload) => void
}

type TPrimeCalendarInfo = {
  queryStartDate: string
  queryEndDate: string
  rangePickerStartDate: Nullable<Date | null>
}

type TProps = IAbsenceProps | IGeneralAbsenceProps | IAbsenceRequestProps

export const AbsenceFormBase = ({
  data,
  type,
  newPayload,
  overviewPath,
  pageTitle,
  isSelectPerson,
  requestStatus,
}: TProps) => {
  const params = useParams<{ personUuid: string }>()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedDateRange, setSelectedDateRange] =
    useState<Nullable<(Date | null)[]>>(null)
  const [startTime, setStartTime] = useState<Moment>(moment().startOf('d'))
  const [endTime, setEndTime] = useState<Moment>(
    moment().endOf('d').set('h', 23).set('m', 45).set('s', 0),
  )
  const [calendarInfo, setCalendarInfo] = useState<TPrimeCalendarInfo>({
    queryStartDate: moment().startOf('month').format('YYYY-MM-DD'),
    queryEndDate: moment().add(1, 'month').endOf('month').format('YYYY-MM-DD'),
    rangePickerStartDate: moment().startOf('month').toDate(),
  })

  const queryData = useQuery({
    queryKey: ['absences-type'],
    queryFn: useAbsenceTypeService.getList,
  })

  const { windowWidth } = useWindowDimensions()

  useEffect(() => {
    if (queryData.status === 'success') {
      const newAbsenceTypeUuid = resolveAbsenceTypeUuid('personal_holiday')
      setAbsenceTypeUuid(newAbsenceTypeUuid)
    }
  }, [queryData.status, queryData.data])

  const resolveAbsenceTypeUuid = (typeKey: string): string => {
    if (queryData.data) {
      const absenceTypesList = queryData.data.list.find(
        e => typeof e !== 'undefined',
      )

      if (!absenceTypesList) return ''

      const absenceByType = absenceTypesList.types.find(
        absence => absence.key === typeKey,
      )

      return absenceByType ? absenceByType.uuid : ''
    }

    return ''
  }

  const [absenceTypeUuid, setAbsenceTypeUuid] = useState<string>('')
  const [personUuid, setPersonUuid] = useState<string>(
    params.personUuid ? params.personUuid : '',
  )
  const [reason, setReason] = useState<string>('')

  const history = useHistory()

  const generalAbsenceQuery = useGetGeneralAbsenceCalendarDataQuery(
    calendarInfo.queryStartDate,
    calendarInfo.queryEndDate,
  )
  const specificAbsenceQuery = useGetAbsenceCalendarDataQuery(
    calendarInfo.queryStartDate,
    calendarInfo.queryEndDate,
  )

  const activeQuery =
    type === 'general-absence'
      ? generalAbsenceQuery.data
      : specificAbsenceQuery.data?.filter(e => e.personUuid === personUuid)

  const removeQueryCache = () => {
    if (type === 'general-absence') {
      queryClient.removeQueries({
        queryKey: ['calendar-general-absences'],
        exact: true,
      })
    } else {
      queryClient.removeQueries({
        queryKey: ['calendar-absences'],
        exact: true,
      })
    }
  }

  const { status } = useGeAbsenceTypeListQuery()

  useEffect(() => {
    if (data) {
      setSelectedDateRange([data.startsAt.toDate(), data.endsAt.toDate()])
      setAbsenceTypeUuid(data.absenceTypeUuid)
      setReason(data.reason)
      setCalendarInfo({...calendarInfo, rangePickerStartDate: data.startsAt.toDate()})
      if (data.personUuid) {
        setPersonUuid(data.personUuid)
      }
    }
  }, [data])

  useEffect(() => {
    if (type === 'general-absence') {
      generalAbsenceQuery.refetch()
    } else {
      specificAbsenceQuery.refetch()
    }
  }, [calendarInfo])

  const handleTypeUuid = (typeUuid: string) => setAbsenceTypeUuid(typeUuid)

  const handleMonthChange = (e: CalendarViewChangeEvent) => {
    setCalendarInfo({
      queryStartDate: moment(e.value).startOf('month').format('YYYY-MM-DD'),
      queryEndDate: moment(e.value)
        .add(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD'),
      rangePickerStartDate: e.value,
    })
    return e.originalEvent
  }

  const handleSave = () => {
    const submitData: IGenerateAbsenceBasePayload = {
      startDate: moment(selectedDateRange![0]),
      startTime,
      endDate: moment(
        selectedDateRange![1] ? selectedDateRange![1] : selectedDateRange![0],
      ),
      endTime,
      absenceTypeUuid,
      reason,
    }

    if (type === 'absence') {
      const generateAbsencePayload = createAbsencePayload({
        ...submitData,
        personUuid,
      })

      if (!generateAbsencePayload.complete) {
        alert(generateAbsencePayload.reason)
        return
      }
      removeQueryCache()
      newPayload(generateAbsencePayload.payload)
      return
    } else if (type === 'general-absence') {
      const generateGeneralAbsencePayload =
        createGeneralAbsencePayload(submitData)

      if (!generateGeneralAbsencePayload.complete) {
        alert(generateGeneralAbsencePayload.reason)
        return
      }
      removeQueryCache()
      newPayload(generateGeneralAbsencePayload.payload)
      return
    } else if (type === 'absence-request') {
      const generateAbsenceRequestPayload =
        createGeneralAbsencePayload(submitData)

      if (!generateAbsenceRequestPayload.complete) {
        alert(generateAbsenceRequestPayload.reason)
        return
      }
      removeQueryCache()
      newPayload(generateAbsenceRequestPayload.payload)
      return
    }
  }

  if (data && !selectedDateRange) {
    return <Spinner />
  }

  return (
    <Layout>
      {requestStatus === 'pending' ? (
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
          opacity={0.8}
          position={'absolute'}
          width={'100%'}
          height={'100%'}
          zIndex={99}
          backgroundColor={'white'}>
          <SpinnerNew />
        </Flex>
      ) : null}
      <Box m={3} mb={1} p={2}>
        <H1> {t(pageTitle)}</H1>
      </Box>

      <Box m={3} p={2}>
        <Calendar
          inline
          selectionMode="range"
          numberOfMonths={windowWidth > 1170 ? 2 : 1}
          viewDate={calendarInfo.rangePickerStartDate}
          onViewDateChange={handleMonthChange}
          dateTemplate={e =>
            DateTemplate(
              e,
              activeQuery || [],
              t,
              selectedDateRange,
              data ? [data.startsAt.toDate(), data.endsAt.toDate()] : null,
            )
          }
          onChange={e => {
            setSelectedDateRange(e.value)
          }}
          value={selectedDateRange}
        />
      </Box>
      {isSelectPerson ? (
        <Box m={1} p={2}>
          <H4 mb={3}>{t('Select a person')}</H4>
          <PersonSelect
            width={500}
            onChange={setPersonUuid}
            value={personUuid}
          />
        </Box>
      ) : null}
      <Box m={1} p={2}>
        <Flex>
          <Flex alignItems={'center'}>
            <H4 marginRight={2}>{t('From')}</H4>
            <TimePicker
              minutesStep={15}
              skipDisabled
              value={data?.startsAt ? data.startsAt : startTime}
              onChange={e => {
                setStartTime(e!)
                return e
              }}
            />
          </Flex>
          <Flex alignItems={'center'}>
            <H4 mr={2} ml={2}>
              {t('To')}
            </H4>
            <TimePicker
              minutesStep={15}
              skipDisabled
              value={data?.endsAt ? data.endsAt : endTime}
              onChange={e => {
                setEndTime(e!)
                return e
              }}
            />
          </Flex>
        </Flex>
      </Box>
      <Box m={1} p={2}>
        <H4 mb={3}>{t('Select an absence type')}</H4>
        <AbsenceTypeSelect
          width={500}
          onChange={handleTypeUuid}
          value={absenceTypeUuid}
        />
      </Box>
      <Box m={1} p={2}>
        <TextArea
          height={175}
          placeholder="Reason for absence..."
          py="4px"
          resize={false}
          type="text"
          width={500}
          onChange={e => setReason(e.target.value)}
          value={reason}
        />
      </Box>
      <Box m={1} p={2}>
        <Flex style={{ gap: '10px' }}>
          <Button onClick={handleSave}>{t('Save')}</Button>
          <Button
            bg={'rgba(0, 0, 0, 0.3)'}
            onClick={() => history.push(overviewPath)}>
            {t('Cancel')}
          </Button>
        </Flex>
      </Box>
    </Layout>
  )
}
